// Configurable completion flows: Ops-side mirror of the backend schemas.

export type FlowStepKind =
  | 'photo_set'
  | 'single_photo'
  | 'choice'
  | 'number'
  | 'text'
  | 'boolean'
  | 'acknowledge'
  | 'signature';

export type FlowType = 'supplier_collection' | 'facility_offload';
export type FlowStage = 'arrival' | 'loading' | 'travel' | 'at_facility' | 'weighing';
export type FlowStatus = 'draft' | 'active' | 'superseded' | 'archived';

export interface FlowStepType {
  id: string;
  key: string;
  kind: FlowStepKind;
  semantic: string | null;
  labels: Record<string, string>;
  help_text?: Record<string, string> | null;
  default_config: Record<string, unknown>;
  applies_to: FlowType[];
  is_system: boolean;
  status: 'active' | 'archived';
}

export interface FlowStepInput {
  /** Unique per flow; answers are recorded under it. */
  step_key: string;
  /** Which catalogue type this step is an instance of; defaults to step_key. */
  step_type_key?: string;
  stage: FlowStage;
  is_required: boolean;
  config: Record<string, unknown>;
  label_overrides?: Record<string, string> | null;
}

export interface CollectionFlowStep extends FlowStepInput {
  id: string;
  step_order: number;
  flow_step_type_id: string;
}

export interface CollectionFlow {
  id: string;
  owner_org_id: string;
  flow_type: FlowType;
  supplier_id: string | null;
  facility_id: string | null;
  version: number;
  status: FlowStatus;
  name: string | null;
  steps: CollectionFlowStep[];
}

export interface FlowContract {
  flow_type: FlowType;
  required_semantics: string[];
  /** Groups where at least one member must be present and required, e.g. the weight source. */
  any_of_semantics?: string[][];
  invoice_required: boolean;
}

export interface ResolvedFlow {
  flow_id: string | null;
  version: number | null;
  flow_type: FlowType;
  is_default: boolean;
  steps: Array<{
    step_key: string;
    step_type_key?: string;
    kind: string;
    semantic: string | null;
    stage: string;
    required: boolean;
    config: Record<string, unknown>;
    labels: Record<string, string>;
  }>;
}

/** Collection steps sit around the two real server checkpoints; offload stages are presentational. */
export const STAGES_BY_FLOW_TYPE: Record<FlowType, FlowStage[]> = {
  supplier_collection: ['arrival', 'loading'],
  facility_offload: ['travel', 'at_facility', 'weighing'],
};

export function stepTypeLabel(stepType: FlowStepType, language: string): string {
  return stepType.labels?.[language] || stepType.labels?.en || stepType.key;
}
